import { faCommentDots, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import {
    faArrowDownToLine,
    faLocationDot,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getBarcode } from "api/IamApi/entities/ezpay";
import { CASH_MODAL } from "constants/i18n-namespaces";
import { EMPTY, ERROR } from "constants/Status";
import { useRetailer } from "hooks";
import html2canvas from "html2canvas";
import { Banner, IconButton, TextButton } from "kubra-ux-forge";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getRetailers, getRetailersLoadStatus } from "selectors";
import { IBiller } from "types/biller";
import { IRetailer } from "types/retailer";
import { account } from "utilities/preload-state";
import { BarcodeView } from "../barcode-view";
import { EmailView } from "../email-view";
import { RetailerView } from "../retailer-view";
import { SMSView } from "../sms-view";


export interface ICashModalProps {
  biller: IBiller;
  accountNumber: string;
  billerLogo: string;
  flowCallback: any;
  realm: string
}

export const CashModal = (props: ICashModalProps) => {
  const { biller, accountNumber, billerLogo, flowCallback, realm } = props;
  const { t } = useTranslation(CASH_MODAL);
  const [isRetailerView, setIsRetailerView] = useState(false);
  const [barcodeView, setBarcodeView] = useState(true);
  const [emailView, setEmailView] = useState(false);
  const [smsView, setSmsView] = useState(false);
  const loadRetailerStatus = useSelector(getRetailersLoadStatus);
  const { loadRetailers } = useRetailer();
  const [barcodeText, setBarcodeText] = useState("init");
  const [barcodeLoadStatus, setBarcodeLoadStatus] = useState("loading");
  const [retailerSlice, setRetailerSlice] = useState<IRetailer[]>();
  const [fullRetailers, setFullRetailers] = useState<IRetailer[]>();
  const retailers = useSelector(getRetailers);
  const [serviceFee, setServiceFee] = useState(0);

  // detect apple device
  const isAppleDevice = /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);

  const setView = (method: string) => {
    if (method === "EmailView") {
      setBarcodeView(false);
      setEmailView(true);
      setSmsView(false);
    } else if (method === "SMSView") {
      setBarcodeView(false);
      setEmailView(false);
      setSmsView(true);
    } else {
      setBarcodeView(true);
      setEmailView(false);
      setSmsView(false);
    }
  };

  const setRetailerFlow = (
    isRetailer: boolean | ((prevState: boolean) => boolean)
  ) => {
    setIsRetailerView(isRetailer);
    if (isRetailer) {
      flowCallback("RetailerModal");
    }
    else (flowCallback("CashModal"));
  };

  const downloadBarcode = () => {
    // @ts-ignore
    html2canvas(document.getElementById("cashmodalbarcode"), {
      useCORS: true,
      onclone: function (doc) {
        const hiddenDiv = doc.getElementById("cashmodalbarcode");
        // @ts-ignore
        hiddenDiv.style.display = "block";
      },
    })
      .then((canvas) => {
        const image = canvas.toDataURL("image/png", 1.0);
        return image;
      })
      .then((image) => {
        const downloadLink = document.createElement("a");
        downloadLink.href = image;
        downloadLink.download =
          biller.billerName + " Payment Barcode.png";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
      });
  };

  useEffect(() => {
    if (loadRetailerStatus === EMPTY) {
      loadRetailers(biller.spaceKrn, biller.clientId, biller.realm);
    } else if (loadRetailerStatus === ERROR) {
      console.log("Error retrieving retailers for RCP");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadRetailerStatus,
    biller.clientId,
  ]);

  useEffect(() => {
    if (retailers && biller.clientId) {
      getBarcode(biller.clientId, biller.realm)
        .then((res: any) => {
          setBarcodeText(res.barcode);
          setServiceFee(res.serviceFee);
          setBarcodeLoadStatus("loaded");
        })
        .catch((err) => {
          setBarcodeLoadStatus("error");
        });
      if (retailerSlice === undefined || retailerSlice.length === 0) {
        setFullRetailers(retailers);
        if (retailers.length > 3) {
          setRetailerSlice(
            retailers?.filter((retailer) => {
              return retailer.enabled === true && retailer.preferred === true;
            })
              .sort((a, b) => a.orderNo - b.orderNo)
              .slice(0, 3)
          );

          setFullRetailers(
            retailers?.filter((retailer) => {
              return retailer.enabled === true;
            })
              .sort((a, b) => a.orderNo - b.orderNo)
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biller.clientId, retailers])

  return (
    <div>
      {!isRetailerView && barcodeLoadStatus == "error" && (
        <div className="barcode-error-container">
          <Banner
            messageLabel={`${t("barcode-generation-error")}`}
            variant="error"
            data-cy="banner-error-content"
            withIcon
          />
        </div>
      )}
      {!isRetailerView && (
        <div className="cash-modal">
          {barcodeLoadStatus !== "error" && (
            <div className="cash-primary-display">
              {barcodeView && (
                <div data-testid="barcode-view">
                  <BarcodeView
                    billerLogo={billerLogo}
                    billerName={biller.billerName}
                    accountNumber={accountNumber}
                    barcodeText={barcodeText}
                    serviceFee={serviceFee}
                  /></div>
              )}
              {emailView && (
                <EmailView
                  cancelHandler={setView}
                />
              )}
              {smsView && (
                <SMSView
                  billerName={biller.billerName}
                  cancelHandler={setView}
                  accountNumber={accountNumber}
                  barcodeText={barcodeText}
                />
              )}
            </div>
          )}
          <div></div>
          {barcodeLoadStatus !== "error" && (
            <div className="cash-method">
              <div className="content-row">
                <div className="save-barcode-container-title">
                  {t("save-barcode-label")}
                </div>
              </div>
              <div className="save-barcode-icons-container">
                <IconButton
                  icon={<FontAwesomeIcon icon={faEnvelope} />}
                  label={`${t("get-barcode-email-label")}`}
                  onClick={() => {
                    setView("EmailView");
                  }}
                  className="icon-button"
                  title={`${t("get-barcode-email-button-title")}`}
                  dataCy="icon-button-email-view"
                />
                <IconButton
                  icon={<FontAwesomeIcon icon={faCommentDots} />}
                  label={`${t("get-barcode-sms-label")}`}
                  onClick={() => {
                    setView("SMSView");
                  }}
                  className="icon-button"
                  title={`${t("get-barcode-sms-button-title")}`}
                  dataCy="icon-button-sms-view"
                />
                <IconButton
                  icon={<FontAwesomeIcon icon={faArrowDownToLine} />}
                  label={`${t("download-barcode-label")}`}
                  onClick={() => downloadBarcode()}
                  className="icon-button"
                  title={`${t("download-barcode-button-title")}`}
                />
              </div>
            </div>
          )}
          <div className="cash-retailers">
            <div className="content-row">
              <div className="save-barcode-container-title">
                {retailers !== undefined && retailers.some(item => item.preferred) && t("pay-with-retailers-label")}
              </div>
              <div></div>
            </div>
            <div
              className="retailer-logos"
              title={`${t("retailer-logos-title")}`}
            >
              {retailerSlice &&
                retailerSlice.map((retailer) => (
                  <img
                    key={retailer.id}
                    src={retailer.logo}
                    alt={retailer.name}
                    className="retailer-logo-img"
                  />
                ))}
            </div>
            <div className="content-row">
              <TextButton
                icon={<FontAwesomeIcon icon={faLocationDot} />}
                onClick={() => setRetailerFlow(true)}
                className="text-button"
                data-testid="viewRetailerButton"
                dataCy="button-find-a-retailer"
              >
                {t("find-a-retailer-label")}
              </TextButton>
            </div>
          </div>
        </div>
      )}
      {isRetailerView && (
        <div>
          <div className="retail-view">
            <RetailerView
              defaultRetailerId="0"
              defaultZipCode={account.validation?.mailingAddress?.postalCode ?? ""}
              mapMode={false}
              retailers={fullRetailers}
              isOpen={true}
            />
          </div>
        </div>
      )}
      <div
        id="cashmodalbarcode"
        className="barcode-hidden"
        aria-hidden="true"
        title="hidden barcode"
      >
        <div className="cash-primary-display">
          <BarcodeView
            billerName={biller.billerName}
            billerLogo={billerLogo}
            accountNumber={accountNumber}
            barcodeText={barcodeText}
            serviceFee={serviceFee}
          />
        </div>
      </div>
    </div>
  );
};
export default CashModal;
